// Portada de LOGÍSTICA: qué sale hoy, si está listo y qué lo bloquea.
//
// Existe aparte y no como una tercera rama de `DashboardV2Page` (1.371 líneas
// con condicionales de `sinProduccion` ya repartidos): un tercer eje allí lo
// vuelve ilegible, y esto no comparte ni un bloque con aquella portada.
//
// No pide nada nuevo al servidor: reusa la MISMA consulta de cargas que la
// pantalla de cargas de salida —misma `queryKey`, así que entre las dos hay un
// solo viaje— y los mismos criterios. Ver `utils/inicioLogistica.ts`.
//
// ── Sobre el diseño ─────────────────────────────────────────────────────────
// La jerarquía va por ESCALA Y POSICIÓN, no por decoración: una banda ancha
// para HOY (la pregunta de la mañana), debajo la semana en pequeño, y al final
// las dos listas de trabajo. La primera versión eran cuatro tarjetas iguales y
// dos bloques iguales — todo con el mismo peso, que es exactamente lo que hace
// que una pantalla se lea plana: nada dice por dónde empezar.
//
// La paleta es la de los ESTADOS de carga (`ESTADO_CHIP`):
// verde preparado, azul en preparación, gris sin empezar, rojo la alarma. No se inventa
// ningún color: la barra de hoy y los chips de la tabla de cargas se enseñan
// el uno al otro, y un tono nuevo aquí sería un código más que aprender.
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAppStore } from '@/store'
import { useCargasSalida } from '@/hooks/useCargasSalida'
import { useIsEmpresaAll } from '@/hooks/useIsEmpresaAll'
import { Loader } from '@/components/common/Loader'
import { RefreshButton } from '@/components/common/RefreshButton'
import { resumenLogistica, type BandaDia, type ResumenDia } from '@/utils/inicioLogistica'
import { CHAPA_RIESGO, ESTADO_CHIP, reservaDe, retrasoDe } from '@/utils/cargasSalida'
import type { CargaSalida } from '@/types'

const num = (n: number) => n.toLocaleString('es-ES')

export function InicioLogisticaPage() {
  const { t, i18n } = useTranslation()
  const { dark } = useAppStore()
  const isEmpresaAll = useIsEmpresaAll()
  const { cargas, isLoading, isError, isFetching, refetch } = useCargasSalida({})

  const r = useMemo(() => resumenLogistica(cargas), [cargas])
  const hoy = new Date()

  const superficie = dark ? 'bg-slate-900/60 border-white/8' : 'bg-white border-slate-200 shadow-sm'
  const tenue = dark ? 'text-slate-400' : 'text-slate-500'
  const linea = dark ? 'border-white/8' : 'border-slate-200'

  if (isLoading) return <Loader label={t('inicioLogistica.cargando')} />

  return (
    <div className={`flex flex-col gap-5 ${dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'}`}>
      {/* Cabecera propia, no `PageHeader`: aquí el título convive con la fecha
          del día, que es el sujeto de toda la pantalla. */}
      <header className={`flex items-end justify-between gap-3 flex-wrap pb-4 border-b ${linea}`}>
        <div>
          <h1 className="text-xl font-bold leading-tight">{t('inicioLogistica.titulo')}</h1>
          <p className={`text-[12px] ${tenue}`}>
            {hoy.toLocaleDateString(i18n.language, {
              weekday: 'long', day: 'numeric', month: 'long',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isError && <span className="text-[11px] text-red-500 font-semibold">{t('common.error')}</span>}
          <RefreshButton dark={dark} onClick={refetch} isLoading={isFetching} />
        </div>
      </header>

      <BandaHoy dia={r.hoy} dark={dark} superficie={superficie} tenue={tenue} linea={linea} />

      <Semana bandas={r.proximos} dark={dark} superficie={superficie} tenue={tenue} linea={linea} />

      <div className="grid gap-5 xl:grid-cols-2">
        <Trabajo
          dark={dark} superficie={superficie} tenue={tenue} linea={linea}
          titulo={t('inicioLogistica.vanTarde.titulo')}
          ayuda={t('inicioLogistica.vanTarde.ayuda')}
          vacio={t('inicioLogistica.vanTarde.vacio')}
          cargas={r.vanTarde}
          isEmpresaAll={isEmpresaAll}
          urgente
          chapa={(c) => {
            const dias = retrasoDe(c)
            return {
              texto: dias ? t('inicioLogistica.diasTarde', { count: dias }) : t('inicioLogistica.saleHoy'),
              clases: dark ? CHAPA_RIESGO.oscuro : CHAPA_RIESGO.claro,
            }
          }}
        />
        <Trabajo
          dark={dark} superficie={superficie} tenue={tenue} linea={linea}
          titulo={t('inicioLogistica.sinEmpezar.titulo')}
          ayuda={t('inicioLogistica.sinEmpezar.ayuda')}
          vacio={t('inicioLogistica.sinEmpezar.vacio')}
          cargas={r.sinEmpezar}
          isEmpresaAll={isEmpresaAll}
          chapa={() => ({
            texto: t('cargasSalida.estadoCarga.reserva_total'),
            clases: dark ? ESTADO_CHIP.reserva_total.dark : ESTADO_CHIP.reserva_total.light,
          })}
        />
      </div>
    </div>
  )
}

// ── Hoy ──────────────────────────────────────────────────────────────────────

/**
 * La banda del día. Asimétrica a propósito: el recuento de cargas manda por
 * tamaño y lo demás lo acompaña. Cuatro cifras del mismo cuerpo no dicen por
 * dónde empezar — que era el problema de la versión anterior.
 */
function BandaHoy({ dia, dark, superficie, tenue, linea }: {
  dia: ResumenDia; dark: boolean; superficie: string; tenue: string; linea: string
}) {
  const { t } = useTranslation()
  const vacio = dia.cargas === 0

  return (
    <section className={`rounded-xl border overflow-hidden ${superficie}`}>
      <div className="flex flex-wrap items-center gap-x-10 gap-y-4 px-5 py-4">
        <div className="flex items-baseline gap-2.5">
          <span className="text-5xl font-bold tabular-nums leading-none tracking-tight">
            {num(dia.cargas)}
          </span>
          <span className="text-[13px] font-semibold">
            {t('inicioLogistica.hoy.cargas', { count: dia.cargas })}
          </span>
        </div>

        {!vacio && (
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <Dato
              valor={num(dia.palets)} unidad={t('inicioLogistica.hoy.palets')}
              nota={dia.paletsSinDato > 0 ? t('inicioLogistica.sinDato', { n: dia.paletsSinDato }) : undefined}
              tenue={tenue}
            />
            <Dato
              valor={num(Math.round(dia.peso))} unidad="kg"
              nota={dia.pesoSinDato > 0 ? t('inicioLogistica.sinDato', { n: dia.pesoSinDato }) : undefined}
              tenue={tenue}
            />
            <Dato valor={num(dia.clientes)} unidad={t('inicioLogistica.hoy.clientes', { count: dia.clientes })} tenue={tenue} />
          </div>
        )}
      </div>

      {vacio ? (
        <p className={`px-5 pb-4 text-[12px] ${tenue}`}>{t('inicioLogistica.hoy.vacio')}</p>
      ) : (
        <div className={`border-t ${linea} px-5 py-3`}>
          <BarraPreparacion dia={dia} dark={dark} />
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[11px]">
            <Leyenda n={dia.listas} texto={t('cargasSalida.estadoCarga.preparado')} punto="bg-emerald-600" tenue={tenue} />
            <Leyenda n={dia.empezadas} texto={t('cargasSalida.estadoCarga.en_preparacion')} punto="bg-blue-600" tenue={tenue} />
            <Leyenda n={dia.sinEmpezar} texto={t('inicioLogistica.hoy.sinTocar')} punto={dark ? 'bg-slate-600' : 'bg-slate-400'} tenue={tenue} />
          </div>
        </div>
      )}
    </section>
  )
}

function Dato({ valor, unidad, nota, tenue }: {
  valor: string; unidad: string; nota?: string; tenue: string
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-xl font-semibold tabular-nums">{valor}</span>
      <span className={`text-[12px] ${tenue}`}>{unidad}</span>
      {/* El hueco se declara, no se suma como cero: con esta cifra se decide si
          cabe en un camión. Misma disciplina que `totalesCargas`. */}
      {nota && <span className={`text-[10px] ${tenue}`}>({nota})</span>}
    </div>
  )
}

function Leyenda({ n, texto, punto, tenue }: { n: number; texto: string; punto: string; tenue: string }) {
  if (n === 0) return null
  return (
    <span className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${punto}`} />
      <span className="font-semibold tabular-nums">{num(n)}</span>
      <span className={tenue}>{texto}</span>
    </span>
  )
}

/** Barra apilada con los colores del semáforo. No es decoración: es el reparto
 *  de lo que sale hoy, y se lee de un vistazo. */
function BarraPreparacion({ dia, dark }: { dia: ResumenDia; dark: boolean }) {
  const total = Math.max(1, dia.cargas)
  const trozo = (n: number, clase: string) =>
    n > 0 ? <div className={clase} style={{ width: `${(n / total) * 100}%` }} /> : null
  return (
    <div className={`flex h-2 rounded-full overflow-hidden ${dark ? 'bg-white/8' : 'bg-slate-100'}`}>
      {trozo(dia.listas, 'bg-emerald-600')}
      {trozo(dia.empezadas, 'bg-blue-600')}
      {trozo(dia.sinEmpezar, dark ? 'bg-slate-600' : 'bg-slate-400')}
    </div>
  )
}

// ── Los próximos días ────────────────────────────────────────────────────────

/**
 * La semana por delante, en pequeño. Contesta «¿qué se me viene encima?», que
 * no es la misma pregunta que «¿qué saco hoy?» y por eso va aparte y en otro
 * cuerpo de letra.
 *
 * Los días vacíos SE PINTAN: que un jueves no salga nada es información, y
 * saltárselo haría parecer la semana continua cuando no lo es.
 */
function Semana({ bandas, dark, superficie, tenue, linea }: {
  bandas: BandaDia[]; dark: boolean; superficie: string; tenue: string; linea: string
}) {
  const { t, i18n } = useTranslation()
  const tope = Math.max(1, ...bandas.map(b => b.cargas))

  return (
    <section className={`rounded-xl border ${superficie}`}>
      <h2 className={`px-5 pt-3.5 pb-2 text-[12px] font-bold border-b ${linea}`}>
        {t('inicioLogistica.semana.titulo')}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {bandas.map((b, i) => (
          <div
            key={b.fecha ?? 'resto'}
            className={`px-5 py-3 ${i > 0 ? `border-t lg:border-t-0 lg:border-l ${linea}` : ''}`}
          >
            <div className={`text-[11px] font-semibold ${b.fecha ? '' : tenue}`}>
              {b.fecha
                ? new Date(`${b.fecha}T00:00:00`).toLocaleDateString(i18n.language, {
                    weekday: 'short', day: 'numeric',
                  })
                : t('inicioLogistica.semana.resto')}
            </div>
            <div className="flex items-baseline gap-1.5 mt-0.5">
              <span className={`text-lg font-semibold tabular-nums ${b.cargas === 0 ? tenue : ''}`}>
                {num(b.cargas)}
              </span>
              {b.palets > 0 && (
                <span className={`text-[11px] ${tenue}`}>
                  {t('inicioLogistica.semana.palets', { n: num(b.palets) })}
                </span>
              )}
            </div>
            {/* La parte verde es lo que ya está preparado de ese día. */}
            <div className={`mt-1.5 h-1 rounded-full overflow-hidden ${dark ? 'bg-white/8' : 'bg-slate-100'}`}>
              <div className={dark ? 'h-full bg-sky-500/70' : 'h-full bg-sky-500'}
                   style={{ width: `${(b.cargas / tope) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Las dos listas de trabajo ────────────────────────────────────────────────

/** Se enseñan hasta 6: esto es una portada, no la pantalla de cargas. Si hay
 *  más, el pie lleva a la lista entera. */
const MAXIMO = 6

function Trabajo({
  titulo, ayuda, vacio, cargas, dark, superficie, tenue, linea, chapa, isEmpresaAll, urgente,
}: {
  titulo: string; ayuda: string; vacio: string
  cargas: CargaSalida[]
  dark: boolean; superficie: string; tenue: string; linea: string
  isEmpresaAll: boolean
  urgente?: boolean
  chapa: (c: CargaSalida) => { texto: string; clases: string }
}) {
  const { t } = useTranslation()
  // La franja roja marca el bloque urgente, el mismo idioma que `FILA_ALARMA`
  // usa en la tabla. Sólo cuando hay algo: una franja permanente no avisa.
  const franja = urgente && cargas.length > 0
    ? `border-l-4 ${dark ? 'border-l-red-500' : 'border-l-red-600'}`
    : ''

  return (
    <section className={`rounded-xl border overflow-hidden flex flex-col ${superficie} ${franja}`}>
      <div className={`flex items-start justify-between gap-3 px-4 py-2.5 border-b ${linea}`}>
        <div>
          <h2 className="text-[13px] font-bold">{titulo}</h2>
          <p className={`text-[11px] ${tenue}`}>{ayuda}</p>
        </div>
        {cargas.length > 0 && (
          <span className={`text-[13px] font-bold tabular-nums shrink-0 ${
            urgente ? (dark ? 'text-red-400' : 'text-red-600') : ''
          }`}>
            {num(cargas.length)}
          </span>
        )}
      </div>

      {cargas.length === 0 ? (
        <p className={`px-4 py-8 text-[12px] text-center ${tenue}`}>{vacio}</p>
      ) : (
        <>
          <ul className="flex-1">
            {cargas.slice(0, MAXIMO).map((c, i) => {
              const ch = chapa(c)
              const falta = reservaDe(c).falta
              return (
                <li key={`${c.empresa}-${c.carga}`} className={i > 0 ? `border-t ${linea}` : ''}>
                  <Link
                    to={`/cargas-salida?q=${encodeURIComponent(c.carga)}`}
                    className={`flex items-center gap-3 px-4 py-2 transition-colors ${
                      dark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className="font-semibold text-[12px] tabular-nums w-[132px] shrink-0 truncate">
                      {c.carga}
                    </span>
                    <span className={`text-[11px] flex-1 truncate ${tenue}`} title={c.clienteNombre ?? c.cliente ?? ''}>
                      {c.clienteNombre?.trim() || c.cliente || '—'}
                      {isEmpresaAll && <span className="ml-2 uppercase">{c.empresa}</span>}
                    </span>
                    {/* Lo que falta por atar sólo se dice cuando falta algo: un
                        «faltan 0» es ruido en una lista que hay que leer rápido. */}
                    {falta > 0 && (
                      <span className={`text-[11px] tabular-nums shrink-0 ${tenue}`}>
                        {t('inicioLogistica.faltan', { n: num(falta) })}
                      </span>
                    )}
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 ${ch.clases}`}>
                      {ch.texto}
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
          <div className={`px-4 py-2 border-t ${linea}`}>
            <Link to="/cargas-salida" className={`text-[11px] font-semibold ${dark ? 'text-sky-400' : 'text-sky-600'} hover:underline`}>
              {cargas.length > MAXIMO
                ? t('inicioLogistica.verTodas', { n: cargas.length })
                : t('inicioLogistica.irACargas')}
            </Link>
          </div>
        </>
      )}
    </section>
  )
}
